import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    Grid,
    Typography,
    Stack,
    Paper,
    Divider,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {useEffect, useMemo, useState} from "react";
import {DateCalendar} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import logbookApi from "../../../api/staffApi/logbookApi";
import hasPermission from "../../../redux/hasPermission";
import floatParser from "../../../helpers/floatParser";
import Giderler from "./Giderler";
import Transferler from "./Transferler";
import Mesailer from "./Mesailer";
import Cariler from "./Cariler";
import Odemeler from "./Odemeler";
import SatislarCard from "./components/SatislarCard";
import {
    ICalisanOdemeKayit,
    ICariKayit,
    IGider,
    IHesap,
    IHesapHareketi,
    IKalem,
    IMesaiKayit,
    ISatis
} from "../../../constants/types/logbook";
import {useDispatch} from "react-redux";
import {showNotification} from "../../../redux/reducers/notificationReducer";
import {useFormik} from "formik";
import * as Yup from "yup";
import {useGunsonu, useSabitler} from "../../../hooks/queries/useLogbookQueries";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {EventAvailable, Save, Refresh, ExpandMore, CalendarMonth} from "@mui/icons-material";

const InitData = {
    data: [],
    guncellenecekListe: [],
    guncellenecekIdListe: [],
    silinecekIdListe: [],
    eklenenListe: []
}

const validationSchema = Yup.object({
    kasa: Yup.number().required('Kasa tutarı zorunludur').min(0, 'Kasa tutarı negatif olamaz'),
    satislar: Yup.array().of(
        Yup.object({
            miktar: Yup.number().min(0, 'Satış miktarı negatif olamaz')
        })
    )
});

const Gunsonu = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const [tarih, setTarih] = useState<string>(dayjs(new Date(Date.now() - 5 * (60 * 60 * 1000))).format("DD-MM-YYYY"));
    const [kasaBaslangic, setKasaBaslangic] = useState<number>(0);

    const {data: sabitler} = useSabitler();
    const {data: gunsonuData, isLoading: isGunsonuLoading} = useGunsonu(tarih);

    const [giderler, setGiderler] = useState<IKalem<IGider>>(InitData);
    const [cariler, setCariler] = useState<IKalem<ICariKayit>>(InitData);
    const [transferler, setTransferler] = useState<IKalem<IHesapHareketi>>(InitData);
    const [personelOdemeleri, setPersonelOdemeleri] = useState<IKalem<ICalisanOdemeKayit>>(InitData);
    const [mesailer, setMesailer] = useState<IKalem<IMesaiKayit>>(InitData);

    const gunsonuDuzenleYetki = hasPermission("galleon_logbook.gunsonuDuzenle");
    const gecmisGunsonuDuzenleYetki = hasPermission("galleon_logbook.gecmisGunsonuDuzenle");
    const giderlerDuzenle = hasPermission("galleon_logbook.giderlerDuzenle");
    const transferlerDuzenle = hasPermission("galleon_logbook.transferlerDuzenle");
    const odemelerDuzenle = hasPermission("galleon_logbook.odemelerDuzenle");
    const mesailerDuzenle = hasPermission("galleon_logbook.mesailerDuzenle");
    const carilerDuzenle = hasPermission("galleon_logbook.carilerDuzenle");

    const gunsonuTarih = useMemo(() => dayjs(tarih, "DD-MM-YYYY"), [tarih]);

    const filteredHesaplar = useMemo(() => {
        if (!sabitler?.hesaplar) return [];
        return (sabitler.hesaplar as IHesap[]).filter((hesap: IHesap) => {
            const baslangic = hesap.aktif_baslangic ? dayjs(hesap.aktif_baslangic) : null;
            const bitis = hesap.aktif_bitis ? dayjs(hesap.aktif_bitis) : null;
            if (baslangic && gunsonuTarih.isBefore(baslangic, 'day')) return false;
            return !(bitis && gunsonuTarih.isAfter(bitis, 'day'));
        });
    }, [sabitler, gunsonuTarih]);

    const isletmeHesaplari = useMemo(() => filteredHesaplar?.filter((x: IHesap) => x.hesapTipi === "İşletme") ?? [], [filteredHesaplar]);
    const kasaHesap = useMemo(() => isletmeHesaplari.find((x: IHesap) => x.hesapAdi === "Kasa") ?? {
        id: 1,
        hesapAdi: "Kasa"
    }, [isletmeHesaplari]);

    const mutation = useMutation({
        mutationFn: (payload: any) => logbookApi.setGunsonu(payload),
        onSuccess: () => {
            dispatch(showNotification({message: 'Gün sonu başarıyla kaydedildi.', severity: 'success'}));
            queryClient.invalidateQueries({queryKey: ['gunsonu', tarih]});
        }
    });

    const validateKalemler = (): boolean => {
        const checkList = (list: any[], type: string) => {
            for (const item of list) {
                if (type === 'gider' && (!item.tip || !item.kaynak || !item.miktar)) return "Giderlerde eksik alanlar var.";
                if (type === 'transfer' && (!item.kaynak || !item.hedef || !item.miktar)) return "Transferlerde eksik alanlar var.";
                if (type === 'odeme' && (!item.kisi || !item.kaynak || !item.miktar)) return "Ödemelerde eksik alanlar var.";
                if (type === 'mesai' && (!item.kisi || !item.baslangic_saat)) return "Mesailerde eksik alanlar var.";
                if (type === 'cari' && (!item.kisi || !item.miktar)) return "Cari kayıtlarda eksik alanlar var.";
            }
            return null;
        };

        const error = checkList(giderler.eklenenListe, 'gider') || checkList(Object.values(giderler.guncellenecekListe), 'gider') ||
            checkList(transferler.eklenenListe, 'transfer') || checkList(Object.values(transferler.guncellenecekListe), 'transfer') ||
            checkList(personelOdemeleri.eklenenListe, 'odeme') || checkList(Object.values(personelOdemeleri.guncellenecekListe), 'odeme') ||
            checkList(mesailer.eklenenListe, 'mesai') || checkList(Object.values(mesailer.guncellenecekListe), 'mesai') ||
            checkList(cariler.eklenenListe, 'cari') || checkList(Object.values(cariler.guncellenecekListe), 'cari');

        if (error) {
            dispatch(showNotification({message: error, severity: 'error'}));
            return false;
        }
        return true;
    };

    const formik = useFormik({
        initialValues: {
            kasa: 0,
            satislar: [] as ISatis[]
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (!validateKalemler()) return;
            const kaydetVerisiAl = <T, >(kalem: IKalem<T>) => {
                const {guncellenecekListe, silinecekIdListe, eklenenListe} = kalem;
                return {guncellenecekListe, silinecekIdListe, eklenenListe};
            }

            const payload = {
                tarih,
                satislar: values.satislar,
                kasa: values.kasa,
                giderler: kaydetVerisiAl<IGider>(giderler),
                transferler: kaydetVerisiAl<IHesapHareketi>(transferler),
                calisan_odemeleri: kaydetVerisiAl<ICalisanOdemeKayit>(personelOdemeleri),
                calisanlar: kaydetVerisiAl<IMesaiKayit>(mesailer),
                cariler: kaydetVerisiAl<ICariKayit>(cariler),
            };
            mutation.mutate(payload);
        }
    });

    useEffect(() => {
        if (gunsonuData) {
            formik.setFieldValue('kasa', floatParser(gunsonuData.kasa));
            setKasaBaslangic(floatParser(gunsonuData.kasa_baslangic));
            
            // Tüm işletme hesaplarını başlangıçta ekliyoruz ki her biri editlenebilsin
            const allSatislar = isletmeHesaplari
                .filter((q: IHesap) => q.id !== kasaHesap.id)
                .map((hesap: IHesap) => {
                    const existingSatis = gunsonuData.satislar?.find((s: ISatis) => s.hesap === hesap.id);
                    return {
                        id: existingSatis?.id,
                        hesap: hesap.id,
                        miktar: existingSatis ? floatParser(existingSatis.miktar) : 0
                    };
                });
            
            formik.setFieldValue('satislar', allSatislar);
            setGiderler({...InitData, data: gunsonuData.giderler});
            setCariler({...InitData, data: gunsonuData.cariler ?? []});
            setTransferler({...InitData, data: gunsonuData.hesap_hareketleri ?? []});
            setPersonelOdemeleri({...InitData, data: gunsonuData.calisan_odemeleri ?? []});
            setMesailer({...InitData, data: gunsonuData.calisanlar ?? []});
        }
    }, [gunsonuData, kasaHesap.id, isletmeHesaplari]);

    const {nakitSatis, toplamSatis} = useMemo(() => {
        let nSatis = floatParser(formik.values.kasa) - floatParser(kasaBaslangic);
        const calculateSum = (kalem: IKalem<any>, filterId: number) => {
            let sum = 0;
            kalem.data.filter(q => q.kaynak === filterId).forEach(q => {
                if (!kalem.silinecekIdListe.includes(q.id ?? -1) && !kalem.guncellenecekIdListe.includes(q.id ?? -1)) sum += floatParser(q.miktar);
                else if (kalem.guncellenecekIdListe.includes(q.id ?? -1)) sum += floatParser(kalem.guncellenecekListe[q.id!]?.miktar ?? 0);
            });
            kalem.eklenenListe.filter(q => q.kaynak === filterId).forEach(q => sum += floatParser(q.miktar));
            return sum;
        };

        const kasaGiderlerToplami = calculateSum(giderler, kasaHesap.id);
        const kasaCalisanOdemeleriToplami = calculateSum(personelOdemeleri, kasaHesap.id);

        let kasaTransferleriToplami = 0;
        transferler.data.forEach((q: IHesapHareketi) => {
            const isSilinmemis = !transferler.silinecekIdListe.includes(q.id ?? -1) && !transferler.guncellenecekIdListe.includes(q.id ?? -1);
            const miktar = isSilinmemis ? floatParser(q.miktar) : (transferler.guncellenecekIdListe.includes(q.id ?? -1) ? floatParser(transferler.guncellenecekListe[q.id!]?.miktar ?? 0) : 0);
            if (q.kaynak === kasaHesap.id) kasaTransferleriToplami += miktar;
            if (q.hedef === kasaHesap.id) kasaTransferleriToplami -= miktar;
        });
        transferler.eklenenListe.forEach((q: IHesapHareketi) => {
            if (q.kaynak === kasaHesap.id) kasaTransferleriToplami += floatParser(q.miktar);
            if (q.hedef === kasaHesap.id) kasaTransferleriToplami -= floatParser(q.miktar);
        });

        nSatis += (kasaGiderlerToplami + kasaTransferleriToplami + kasaCalisanOdemeleriToplami);
        let tSatis = nSatis;
        formik.values.satislar.forEach((q: ISatis) => {
            if (q.hesap !== kasaHesap.id) tSatis += floatParser(q.miktar);
        });

        return {nakitSatis: nSatis, toplamSatis: tSatis};
    }, [formik.values.kasa, formik.values.satislar, kasaBaslangic, giderler, transferler, personelOdemeleri, kasaHesap.id]);

    if (!sabitler) return null;

    return (
        <Box height={"100%"} sx={{ display: 'flex', flexDirection: 'column' }}>
            {gunsonuDuzenleYetki && (
                <>
                    <Box sx={{
                        flexGrow: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        p: {xs: 1, md: 1.5},
                        pb: 5
                    }}>
                        {isGunsonuLoading ? (
                            <Box sx={{display: 'flex', justifyContent: 'center', mt: 5}}><CircularProgress/></Box>
                        ) : (
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} md={5}>
                                    <Accordion 
                                        defaultExpanded={isMdUp}
                                        elevation={0}
                                        sx={{ 
                                            borderRadius: '16px !important', 
                                            border: '1px solid rgba(0,0,0,0.05)',
                                            overflow: 'hidden',
                                            '&:before': { display: 'none' }
                                        }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CalendarMonth color="primary" />
                                                <Typography variant="h6" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                                    Tarih: {tarih}
                                                </Typography>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0, display: 'flex', justifyContent: 'center' }}>
                                            <DateCalendar
                                                value={dayjs(tarih, "DD-MM-YYYY")}
                                                onChange={(newValue) => setTarih(newValue.format("DD-MM-YYYY"))}
                                                disableFuture
                                                minDate={gecmisGunsonuDuzenleYetki ? undefined : dayjs()}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                                
                                <Grid item xs={12} md={7}>
                                    <SatislarCard
                                        formik={formik}
                                        isletmeHesaplari={isletmeHesaplari}
                                        kasaHesap={kasaHesap}
                                        toplamSatis={toplamSatis}
                                        nakitSatis={nakitSatis}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1, opacity: 0.5 }}>
                                        <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary' }}>Detaylı Kayıtlar</Typography>
                                    </Divider>
                                </Grid>

                                {giderlerDuzenle && (
                                    <Grid item xs={12}>
                                        <Giderler 
                                            giderler={giderler} 
                                            setGiderler={setGiderler}
                                            giderTipleri={sabitler.gider_tipleri}
                                            hesaplar={filteredHesaplar.filter((h: IHesap) => ["İşletme", "Yönetici", "Kredi Kartı"].includes(h.hesapTipi))}
                                        />
                                    </Grid>
                                )}
                                
                                {transferlerDuzenle && (
                                    <Grid item xs={12}>
                                        <Transferler 
                                            transferler={transferler}
                                            setTransferler={setTransferler}
                                            hesaplar={filteredHesaplar}
                                        />
                                    </Grid>
                                )}
                                
                                {odemelerDuzenle && (
                                    <Grid item xs={12}>
                                        <Odemeler 
                                            personelOdemeleri={personelOdemeleri}
                                            setPersonelOdemeleri={setPersonelOdemeleri}
                                            hesaplar={filteredHesaplar.filter((h: IHesap) => ["İşletme", "Yönetici", "Çalışan"].includes(h.hesapTipi))}
                                        />
                                    </Grid>
                                )}
                                
                                {mesailerDuzenle && (
                                    <Grid item xs={12}>
                                        <Mesailer
                                            mesailer={mesailer} 
                                            setMesailer={setMesailer}
                                            calisanlar={sabitler.kisiler}
                                        />
                                    </Grid>
                                )}
                                
                                {carilerDuzenle && (
                                    <Grid item xs={12}>
                                        <Cariler 
                                            cariler={cariler} 
                                            setCariler={setCariler}
                                            calisanlar={sabitler.kisiler}
                                        />
                                    </Grid>
                                )}
                                
                                {/* Butonların içeriği kapatmaması için boşluk */}
                                <Grid item xs={12} sx={{ mb: 5 }} />
                            </Grid>
                        )}
                    </Box>

                    <Paper 
                        elevation={10} 
                        sx={{ 
                            position: 'fixed', 
                            bottom: 0, 
                            left: 0, 
                            right: 0, 
                            p: 2, 
                            bgcolor: 'background.paper',
                            borderTop: '1px solid rgba(0,0,0,0.1)',
                            zIndex: 1000,
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 2
                        }}
                    >
                        <Button 
                            variant="outlined" 
                            color="secondary"
                            startIcon={<Refresh />}
                            onClick={() => queryClient.invalidateQueries({queryKey: ['gunsonu', tarih]})}
                            sx={{ minWidth: {xs: "auto", sm: "12rem"}, borderRadius: '12px', fontWeight: 700 }}
                        >
                            Yenile
                        </Button>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            startIcon={<Save />}
                            onClick={() => formik.handleSubmit()}
                            sx={{ minWidth: {xs: "auto", sm: "12rem"}, borderRadius: '12px', fontWeight: 700 }}
                            disabled={formik.isSubmitting || mutation.isPending}
                        >
                            Güncelle
                        </Button>
                    </Paper>
                </>
            )}
        </Box>
    );
}

export default Gunsonu;
